import React, { useEffect, useState } from 'react'
import { Input, Tag, useHog } from '@microfrontends/react-components'
import {
    apiGetUnHiddenCompany,
    apiGetUserHiddenCompanyName,
    apiGetUserHiddenDomains,
    apiUpdateUserHiddenCompanyName,
    apiUpdateUserHiddenDomain
} from '../services'
import { DEFAULT_SUCCESS_CODE, UPDATE_TYPE, WAIT_TYPING_MILISECONDS } from '../constants'
import debounce from 'debounce-promise'

import {
    InputArea,
    InputDomain,
    MessageAlert,
    PrefixText,
    StyledButton,
    SuggestItem,
    TagItem,
    TagList
} from './inputGroup.ccs'
import { useStore } from '../../../../store'
import { TInputGroupProps } from '../type'

const companyDefaultLogo = 'https://images.vietnamworks.com/img/company-default-logo.svg'

export default function InputGroup(props: TInputGroupProps) {
    const { isMobile, isInputCompanyName, placeholder, max, trans, prefixText, userToken } = props
    const [value, setValue] = useState('')
    const [isFocus, setIsFocus] = useState(false)
    const [tagList, setTagList] = useState<any[]>([])
    const [listRecommend, setListRecommend] = useState<any>([])
    const { langCode } = useStore().state
    const { tracking } = useHog()

    const debouncedLoadOptions = debounce((inputValue) => loadOptions(inputValue), WAIT_TYPING_MILISECONDS, {
        leading: true
    })
    const getUserHiddenCompanyName = async () => {
        if (!userToken) {
            return
        }
        const userHiddenCompanyNameResponse = await apiGetUserHiddenCompanyName(userToken)
        if (userHiddenCompanyNameResponse?.status?.code !== DEFAULT_SUCCESS_CODE) {
            return
        }
        if (userHiddenCompanyNameResponse?.data !== undefined && userHiddenCompanyNameResponse.data.length > 0) {
            let listHiddenCompany: any[] = []
            userHiddenCompanyNameResponse.data.map((item, index) => {
                listHiddenCompany.push({
                    id: index,
                    name: item.main_name,
                    taxCode: item.tax_code
                })
            })
            setTagList(listHiddenCompany)
        }
    }
    const getUserHiddenDomains = async () => {
        if (!userToken) {
            return
        }
        const userHiddenDomainsResponse = await apiGetUserHiddenDomains(userToken)
        if (userHiddenDomainsResponse?.status?.code !== DEFAULT_SUCCESS_CODE) {
            return
        }
        if (userHiddenDomainsResponse.data !== undefined && userHiddenDomainsResponse.data.length > 0) {
            const listHiddenDomain: any[] = []
            userHiddenDomainsResponse.data.map((item, index) => {
                listHiddenDomain.push({
                    id: index,
                    name: item
                })
            })
            setTagList(listHiddenDomain)
        }
    }
    const doUpdateItem = async (domain, taxCode, type = UPDATE_TYPE.ADD) => {
        if (isInputCompanyName) {
            await apiUpdateUserHiddenCompanyName(userToken, langCode, {
                type: type,
                taxCode: taxCode
            })
            tracking({
                eventName: 'js_profile_management:hide_company_by_name',
                properties: {
                    isAdd: type === UPDATE_TYPE.ADD
                }
            })
            return
        }
        await apiUpdateUserHiddenDomain(userToken, langCode, {
            type: type,
            domain: domain
        })
        tracking({
            eventName: 'js_profile_management:hide_company_by_domain',
            properties: {
                isAdd: type === UPDATE_TYPE.ADD
            }
        })
    }
    const getListRecommend = async (value) => {
        const companyResponse = await apiGetUnHiddenCompany(userToken, langCode, value)
        if (companyResponse.status.code !== DEFAULT_SUCCESS_CODE) {
            return
        }
        if (companyResponse.data !== undefined && companyResponse.data.length > 0) {
            const listRecommendMapped: any = []
            companyResponse.data.map((item) => {
                listRecommendMapped.push({
                    id: item.tax_code,
                    taxCode: item.tax_code,
                    label: item.main_name,
                    name: (
                        <SuggestItem>
                            <img src={item.main_logo !== '' ? item.main_logo : companyDefaultLogo} />
                            <span>{item.main_name}</span>
                        </SuggestItem>
                    )
                })
            })
            setListRecommend(listRecommendMapped)
            return
        }
        // No result
        setListRecommend([
            {
                id: -1,
                name: (
                    <div className='suggest-item loading'>
                        <span>{trans('No result found...')}</span>
                    </div>
                )
            }
        ])
    }

    const handleOnClickSuggestionItem = async (id) => {
        const isMax = max !== undefined && tagList.length === max
        if (isMax) {
            setValue('')
            return
        }
        if (id === -1) {
            return
        }
        // Check duplicate
        const tagListFiltered = tagList.filter((item) => item.id === id)
        if (tagListFiltered.length > 0) {
            return
        }
        // Find result
        const filteredList = listRecommend.filter((item) => item?.id === id)
        if (filteredList.length <= 0) {
            return
        }
        tagList.push({
            id: id,
            name: filteredList[0].label,
            taxCode: filteredList[0].taxCode
        })
        setTagList(tagList)
        setValue('')
        await doUpdateItem('', filteredList[0].taxCode, UPDATE_TYPE.ADD)
    }
    const handleOnInputEnterPress = async () => {
        const isMax = max !== undefined && tagList.length === max
        if (isInputCompanyName) {
            return
        }
        const filteredTagList = tagList.filter((item) => item.name === value)
        if (filteredTagList.length > 0) {
            setValue('')
            return
        }
        if (isMax) {
            return
        }
        if (value.trim() === '') {
            return
        }
        tagList.push({
            id: tagList.length,
            name: value
        })
        setTagList(tagList)
        setValue('')
        await doUpdateItem(value, '', UPDATE_TYPE.ADD)
    }
    const handleOnInputChange = (value) => {
        setValue(prefixText ? value.replaceAll(prefixText, '') : value)
        if (isInputCompanyName) {
            // Set loading text before get list recommend
            setListRecommend([
                {
                    id: -1,
                    name: (
                        <div className='suggest-item loading'>
                            <span>{trans('Loading...')}</span>
                        </div>
                    )
                }
            ])

            debouncedLoadOptions(value)
        }
    }
    const handleOnInputFocusOrBlur = (isBlur = true) => {
        setIsFocus(!isBlur)
    }

    const loadOptions = async (inputValue) => {
        if (inputValue.length < 3) {
            return false
        }
        await getListRecommend(inputValue)

        return true
    }

    useEffect(() => {
        if (isInputCompanyName) {
            getUserHiddenCompanyName()
        } else {
            getUserHiddenDomains()
        }
    }, [isInputCompanyName, userToken])

    const renderTagList = () => {
        if (!tagList || tagList.length === 0) return <></>
        const renderTag = tagList.map((item, index) => {
            return (
                <TagItem key={index}>
                    <Tag
                        label={item?.name}
                        iconType='remove'
                        size='medium'
                        onClick={() => removeTag(item)}
                        isMobile={isMobile}
                        isShowIcon
                    />
                </TagItem>
            )
        })

        return <TagList>{renderTag}</TagList>
    }
    const renderMessageAlert = () => {
        const isShowMaxAlert = max !== undefined && tagList.length === max
        if (isInputCompanyName) {
            return (
                <MessageAlert isDisplay={isShowMaxAlert}>
                    <span>{trans('Max number of {0} companies reached').replace('{0}', max.toString())}</span>
                    <span>{trans('Please delete first before add other companies')}</span>
                </MessageAlert>
            )
        }
        return (
            <MessageAlert isDisplay={isShowMaxAlert}>
                <span>
                    {trans('You can add a maximum of')} <strong>{max}</strong> {trans('email domains')}
                </span>
            </MessageAlert>
        )
    }

    const removeTag = async (item) => {
        const tagListFiltered = tagList.filter((tag) => tag?.id !== item.id)
        setTagList(tagListFiltered)
        if (tagList.length !== tagListFiltered.length) {
            // Removed success
            if (isInputCompanyName) {
                await doUpdateItem('', item.taxCode, UPDATE_TYPE.REMOVE)
            } else {
                await doUpdateItem(item.name, '', UPDATE_TYPE.REMOVE)
            }
        }
    }
    const isShowMaxAlert = max !== undefined && tagList.length === max
    return (
        <div>
            <InputDomain className={isFocus ? 'focus' : ''} isInputDomain={!isInputCompanyName}>
                {!isInputCompanyName && <PrefixText>{prefixText}</PrefixText>}
                <InputArea nonePrefix={isInputCompanyName}>
                    <Input
                        placeholder={placeholder}
                        value={value}
                        onPressEnter={() => handleOnInputEnterPress()}
                        {...(isInputCompanyName && !isShowMaxAlert
                            ? {
                                  listRecommend: listRecommend
                              }
                            : {})}
                        onClickSuggestionItem={(item) => handleOnClickSuggestionItem(item.id)}
                        onChange={handleOnInputChange}
                        onFocus={() => handleOnInputFocusOrBlur(false)}
                        onBlur={() => handleOnInputFocusOrBlur(true)}
                    />
                </InputArea>
                {!isInputCompanyName && (
                    <StyledButton isMobile={isMobile} onClick={handleOnInputEnterPress} disabled={value === ''}>
                        {trans('Add')}
                    </StyledButton>
                )}
            </InputDomain>

            {renderMessageAlert()}

            {renderTagList()}
        </div>
    )
}
